/** Call queries: list, detail, transcript, summary.
 *
 * SYNC (cross-repo):
 *   - OpenAPI source: hostesa-backend apps/api call controllers
 *   - Regen after backend changes; see hostesa-app/SYNC.md
 *   - Realtime invalidation: packages/realtime-client/src/cache.ts
 *   - Manifest: hostesa/docs/architecture/00-master/16-cross-repo-sync-manifest.md §F, §G
 */

import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Call,
  type CallSummary,
  callSchema,
  callSummarySchema,
  type Paginated,
  paginated,
  type ToolCall,
  type TranscriptSegment,
  toolCallSchema,
  transcriptSegmentSchema,
} from '../responses';
import { unwrap } from './_helpers';

/**
 * Server-side filter, search and pagination params for the calls list.
 * Type alias (not interface) so it carries an implicit index signature for
 * scoped query keys.
 */
export type CallsFilters = {
  status?: Call['status'];
  direction?: 'inbound' | 'outbound';
  language?: string;
  /** Free-text match against the call id. */
  q?: string;
  /** ISO started-at lower bound (inclusive). */
  from?: string;
  /** ISO started-at upper bound (inclusive). */
  to?: string;
  /** Page size, 1–200 (backend default 50). */
  limit?: number;
  /** Row offset (default 0). */
  offset?: number;
};

/** `GET /calls` — filtered, searched, paginated calls for the hotel (newest first). */
export function useCalls(
  tenantId: string,
  hotelId: string,
  filters?: CallsFilters,
): UseQueryResult<Paginated<Call>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.calls(tenantId, hotelId, filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls', {
        params: {
          path: { tenantId, hotelId },
          query: {
            status: filters?.status,
            direction: filters?.direction,
            language: filters?.language,
            q: filters?.q,
            from: filters?.from,
            to: filters?.to,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(callSchema));
    },
  });
}

/** `GET /calls/:callId` — a single call. */
export function useCall(tenantId: string, hotelId: string, callId: string): UseQueryResult<Call> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.call(tenantId, hotelId, callId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls/{callId}',
        { params: { path: { tenantId, hotelId, callId } }, parseAs: 'json' },
      );
      return unwrap(result, callSchema);
    },
    enabled: callId.length > 0,
  });
}

/** `GET /calls/:callId/transcript` — ordered transcript segments. */
export function useCallTranscript(
  tenantId: string,
  hotelId: string,
  callId: string,
): UseQueryResult<TranscriptSegment[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.callTranscript(tenantId, hotelId, callId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls/{callId}/transcript',
        { params: { path: { tenantId, hotelId, callId } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(transcriptSegmentSchema));
    },
    enabled: callId.length > 0,
  });
}

/**
 * `GET /calls/:callId/tool-calls` — the AI Tool Gateway decision timeline for a
 * call (oldest-first, payload-free). Read-only knowledge lookups are not audited
 * and never appear.
 */
export function useCallToolCalls(
  tenantId: string,
  hotelId: string,
  callId: string,
): UseQueryResult<ToolCall[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.callToolCalls(tenantId, hotelId, callId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls/{callId}/tool-calls',
        { params: { path: { tenantId, hotelId, callId } }, parseAs: 'json' },
      );
      return unwrap(result, z.array(toolCallSchema));
    },
    enabled: callId.length > 0,
  });
}

/**
 * `GET /calls/:callId/summary`. 404s until a summary exists, so callers should
 * treat {@link ApiError.isNotFound} as "not ready yet", not a hard error.
 */
export function useCallSummary(
  tenantId: string,
  hotelId: string,
  callId: string,
): UseQueryResult<CallSummary> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.callSummary(tenantId, hotelId, callId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/calls/{callId}/summary',
        { params: { path: { tenantId, hotelId, callId } }, parseAs: 'json' },
      );
      return unwrap(result, callSummarySchema);
    },
    enabled: callId.length > 0,
    retry: false,
  });
}
